/** CLIENTES — la liste et l'ajout. C'est le seul actif de l'indépendante :
 *  on montre le rythme de visite, pas seulement un annuaire. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CarteLien } from "../components/LienPro";
import { Plus, Search, Users } from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import {
  Avatar, Btn, Champ, Choix, Erreur, Feuille, Micro, Pastille, Squelette, Vide, Zone,
} from "../components/Bits";
import { euros, ilYA, initiales, telephoneLisible } from "../lib/format";

const FILTRES = [
  { c: "toutes", l: "Toutes" },
  { c: "fideles", l: "Fidèles" },
  { c: "relancer", l: "À relancer" },
  { c: "risque", l: "À risque" },
];

const garde = {
  toutes: () => true,
  fideles: (c) => c.visites >= 4 && (c.jours_depuis ?? 999) <= 60,
  relancer: (c) => c.visites >= 1 && (c.jours_depuis ?? 0) > 55 && !c.prochain_rdv,
  risque: (c) => c.absences > 0,
};

export default function Clients() {
  const nav = useNavigate();
  const { compte, version, rafraichir } = useApp();
  const [liste, setListe] = useState(null);
  const [q, setQ] = useState("");
  const [tri, setTri] = useState("recent");
  const [filtre, setFiltre] = useState("toutes");
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", phone: "", email: "", notes: "" });
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get("/clients", { params: { q: q || undefined, tri } });
      setListe(data);
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de charger ta clientèle."));
    } finally {
      setCharge(false);
    }
  }, [q, tri]);

  useEffect(() => {
    const t = setTimeout(charger, q ? 260 : 0);
    return () => clearTimeout(t);
  }, [charger, version]);

  const creer = async () => {
    if (!form.prenom.trim()) return toast.error("Il me faut au moins un prénom.");
    setEnvoi(true);
    try {
      const { data } = await api.post("/clients", {
        prenom: form.prenom.trim(),
        nom: form.nom.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`${data.nom_affiche} est dans ton fichier.`);
      setAjout(false);
      setForm({ prenom: "", nom: "", phone: "", email: "", notes: "" });
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e, "Enregistrement impossible."));
    } finally {
      setEnvoi(false);
    }
  };

  const limite = compte?.plan?.max_clients;
  const total = liste?.length ?? 0;
  const visibles = useMemo(() => (liste || []).filter(garde[filtre]), [liste, filtre]);

  const phrase = useMemo(() => {
    if (!liste || liste.length === 0) return "";
    const aRelancer = liste.filter(garde.relancer).length;
    const aRisque = liste.filter(garde.risque).length;
    const morceaux = [`${total} personne${total > 1 ? "s" : ""} dans ton fichier.`];
    if (aRelancer) morceaux.push(`${aRelancer} s'éloigne${aRelancer > 1 ? "nt" : ""} — un message suffit souvent.`);
    else if (aRisque) morceaux.push(`${aRisque} a déjà posé un lapin.`);
    else morceaux.push("Tout le monde revient.");
    return morceaux.join(" ");
  }, [liste, total]);

  return (
    <div data-testid="page-clients">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Micro>Clientèle</Micro>
          <h1 className="display text-[26px] md:text-[30px] mt-1.5 truncate" data-testid="titre-clients">
            {total > 0 ? `${total} client${total > 1 ? "s" : ""}` : "Tes clientes"}
          </h1>
        </div>
        <Btn icone={Plus} onClick={() => setAjout(true)} data-testid="btn-ajouter-client">
          Ajouter
        </Btn>
      </div>

      {phrase && (
        <p className="text-[16px] md:text-[18px] leading-[1.5] mt-5 max-w-[46ch] pose" data-testid="phrase-clients">
          {phrase}
        </p>
      )}

      <div className="mt-6">
        <CarteLien />
      </div>

      {/* --------------------------------------------------------- recherche */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <div className="relative flex-1">
          <Search
            size={16}
            strokeWidth={1.8}
            className="absolute left-3.5 top-1/2 -translate-y-1/2"
            style={{ color: "var(--text-subtle)" }}
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Un prénom, un nom, un numéro…"
            data-testid="champ-recherche-client"
            className="w-full h-11 pl-10 pr-3 text-[15px] outline-none focus:border-[var(--accent)]"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              transition: "border-color var(--dur) var(--ease)",
            }}
          />
        </div>
        <div className="sm:w-[210px]">
          <Choix
            valeur={tri}
            surChangement={setTri}
            testid="choix-tri"
            options={[
              { valeur: "recent", label: "Dernière visite" },
              { valeur: "alpha", label: "Ordre alphabétique" },
              { valeur: "depense", label: "Total dépensé" },
            ]}
          />
        </div>
      </div>

      {/* ----------------------------------------------------------- filtres */}
      <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
        {FILTRES.map((f) => {
          const n = (liste || []).filter(garde[f.c]).length;
          const actif = filtre === f.c;
          return (
            <button
              key={f.c}
              onClick={() => setFiltre(f.c)}
              data-testid={`filtre-${f.c}`}
              className="h-9 px-3.5 text-[13px] font-semibold shrink-0 inline-flex items-center gap-1.5"
              style={{
                borderRadius: "var(--radius-full)",
                background: actif ? "var(--accent)" : "var(--surface)",
                color: actif ? "#fff" : "var(--text-muted)",
                border: `1px solid ${actif ? "var(--accent)" : "var(--border)"}`,
                transition: "background-color var(--dur) var(--ease), color var(--dur) var(--ease)",
              }}
            >
              {f.l}
              <span className="num text-[11.5px] opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      {limite && total >= limite * 0.8 && (
        <div
          className="p-4 mt-4"
          style={{
            background: "var(--accent-soft)",
            borderRadius: "var(--radius-lg)",
          }}
        >
          <Micro>Ta limite approche</Micro>
          <p className="text-[14px] mt-2">
            <span className="num">{total}</span> fiches sur <span className="num">{limite}</span>.
            Passe au plan complet pour lever la limite.
          </p>
        </div>
      )}

      {/* ------------------------------------------------------------- liste */}
      <div className="mt-5">
        {charge ? (
          <Squelette lignes={4} />
        ) : erreur ? (
          <Erreur message={erreur} surReessai={charger} />
        ) : total === 0 ? (
          <Vide
            icone={Users}
            titre={q ? "Aucun résultat" : "Ton fichier est vide"}
            texte={
              q
                ? "Personne ne correspond. Vérifie l'orthographe, ou ajoute-la à la main."
                : "Chaque rendez-vous crée la fiche tout seul. Tu peux aussi ajouter quelqu'un à la main."
            }
            action={
              <Btn onClick={() => setAjout(true)} data-testid="btn-vide-ajouter-client">
                Ajouter un client
              </Btn>
            }
          />
        ) : visibles.length === 0 ? (
          <Vide
            icone={Users}
            titre="Personne dans ce filtre"
            texte="C'est plutôt une bonne nouvelle. Reviens sur « Toutes » pour voir ton fichier complet."
            action={
              <Btn variante="fantome" onClick={() => setFiltre("toutes")} data-testid="btn-filtre-toutes">
                Voir toutes
              </Btn>
            }
          />
        ) : (
          <div className="space-y-2">
            {visibles.map((c) => (
              <button
                key={c.id}
                onClick={() => nav(`/clients/${c.id}`)}
                data-testid={`client-${c.id}`}
                className="w-full text-left flex items-center gap-3.5 p-3.5 cible-tactile pose"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-lg)",
                  transition: "border-color var(--dur) var(--ease), transform var(--dur) var(--ease)",
                }}
              >
                <Avatar
                  initiales={initiales(c.prenom, c.nom)}
                  ton={c.absences > 0 ? "brique" : c.visites >= 4 ? "olive" : "neutre"}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold truncate">{c.nom_affiche}</span>
                  <span
                    className="block text-[13px] truncate"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {c.visites > 0
                      ? `${c.visites} visite${c.visites > 1 ? "s" : ""} · ${ilYA(c.jours_depuis)}`
                      : c.phone
                      ? telephoneLisible(c.phone)
                      : "Jamais venu"}
                  </span>
                </span>
                <span className="text-right shrink-0">
                  <span className="num block text-[15px]">{euros(c.depense_totale)}</span>
                  <span className="flex justify-end mt-1">
                    {c.prochain_rdv ? (
                      <Pastille ton="accent">RDV prévu</Pastille>
                    ) : c.absences > 0 ? (
                      <Pastille ton="brique">
                        {c.absences} absence{c.absences > 1 ? "s" : ""}
                      </Pastille>
                    ) : null}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Feuille
        ouvert={ajout}
        surFermeture={() => setAjout(false)}
        micro="Clientèle"
        titre="Ajouter un client"
        testid="feuille-client"
        pied={
          <>
            <Btn variante="nu" onClick={() => setAjout(false)}>
              Annuler
            </Btn>
            <Btn onClick={creer} charge={envoi} data-testid="btn-enregistrer-client">
              Enregistrer
            </Btn>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Champ
              label="Prénom"
              value={form.prenom}
              onChange={(e) => setForm({ ...form, prenom: e.target.value })}
              placeholder="Inès"
              data-testid="champ-client-prenom"
            />
            <Champ
              label="Nom"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Bernard"
              data-testid="champ-client-nom"
            />
          </div>
          <Champ
            label="Mobile"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="06 12 34 56 78"
            inputMode="tel"
            indice="Sans numéro, pas de rappel ni de lien personnel"
            data-testid="champ-client-tel"
          />
          <Champ
            label="E-mail"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="ines@exemple.fr"
            inputMode="email"
            data-testid="champ-client-email"
          />
          <Zone
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Formule couleur, allergie, préférences…"
            data-testid="champ-client-notes"
          />
        </div>
      </Feuille>
    </div>
  );
}
